/* eslint-disable no-await-in-loop */
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { Col, message, Row, notification, Divider, Modal } from 'antd';

import { useTranslation } from 'react-i18next';
import { CheckCircleOutlined } from '@ant-design/icons';
import POSButton from '../POSButton';
import POSContext from '../POSContext';

import {
  cancelOrder,
  getOpenOrders,
  printReceipt,
  setOrderCard,
} from '../../../services/pos/pos';
import {
  downloadImage,
  getNextEnrolledPos,
  print,
  showMessage,
} from '../../../helpers/printing';
import { ejectCard } from '../../../services/stocks/cards';
import { getReceiptByOrderId } from '../../../services/receipts';

const PrintCard = () => {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState({});
  const [openOrders, setOpenOrders] = useState([]);
  const { shift, order, setOrder } = useContext(POSContext);
  const [disabled, setDisabled] = useState(false);

  const getImage = useCallback(
    async (id) => {
      let image = templates[id];
      if (!image) {
        image = await downloadImage(id);
        setTemplates({ ...templates, id: image });
      }
      return image;
    },
    [templates],
  );

  useEffect(() => {
    if (
      openOrders.length > 0 &&
      openOrders.every((o) => o.status === 'CONFIRMED')
    ) {
      setOrder(openOrders[0]);
    }
  }, [openOrders, setOrder]);

  const downloadImages = useCallback(
    async (templateId, userPhotoId) => {
      try {
        const layout = await getImage(templateId);
        const userPhoto = userPhotoId ? await downloadImage(userPhotoId) : null;
        return userPhoto ? [layout, userPhoto] : [layout];
      } catch (err) {
        console.error(err);
        notification.error({
          message: t('entity.pos.actions.inputError'),
        });
      }
      return [];
    },
    [getImage, t],
  );

  const handleCancelOrder = useCallback(() => {
    cancelOrder(shift, order)
      .then((res) => {
        setOrder(res);
      })
      .catch((err) => {
        console.error(err);
        message.error({
          content: 'Eroare la anulare comanda',
          key: 'pos',
          duration: 5,
          className: 'card-message',
        });
      });
  }, [shift, order, setOrder]);

  useEffect(() => {
    getOpenOrders(shift)
      .then((res) => setOpenOrders(res))
      .catch((err) => {
        console.error(err);
        setOpenOrders([]);
      });
  }, [shift]);

  const updateOrder = useCallback(
    async (id, card) =>
      setOrderCard(shift, id, { ...card })
        .then((res) => res)
        .catch((err) => {
          console.error(err);
          message.error({
            content: t('entity.pos.actions.invalidType'),
            key: 'pos',
            duration: 5,
            className: 'card-message',
          });
        }),
    [shift, t],
  );

  const getPayload = useCallback(
    (ord) =>
      (ord.products || [])
        .map((op) => op.payload)
        .flat()
        .filter((op) => op.shouldScan)[0],
    [],
  );

  const beginPrinting = useCallback(async () => {
    setDisabled(true);
    const allNotPrinted = openOrders
      .filter((o) => o.status === 'PRINTING')
      .map((o) => {
        const { id, paymentMethod } = o;
        const { templateId, userPhotoId, userFullname, userIdentityCard } =
          o.account;
        const payloadId = getPayload(o)?.id;
        return {
          templateId,
          userPhotoId,
          userFullname,
          id,
          payloadId,
          userIdentityCard,
          paymentMethod,
        };
      });

    const done = [];
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const pers of allNotPrinted) {
        const {
          templateId,
          userFullname,
          userPhotoId,
          id,
          payloadId,
          userIdentityCard,
          paymentMethod,
        } = pers;
        let shouldContinue = true;
        while (shouldContinue) {
          try {
            showMessage(
              'loading',
              t('entity.pos.successMessages.readingCard'),
              0,
            );
            const card = await getNextEnrolledPos();
            if (!card) {
              handleCancelOrder();
              showMessage('success', 'Comanda a fost anulata cu succes!');
              setDisabled(false);
              return;
            }
            const { physicalId } = card;
            showMessage(
              'loading',
              t('entity.pos.successMessages.printingCard', {
                physicalId,
                userFullname,
              }),
            );
            const images = await downloadImages(templateId, userPhotoId);
            const nr = `${(userIdentityCard || '').substring(0, 7)}`;

            // print first; update order only after successful print
            await print(images, ` \n${nr}\n${userFullname}`);

            const receipe = await getReceiptByOrderId(id);
            const { cuiCode } = receipe;
            const result = await printReceipt(
              paymentMethod,
              shift.posId,
              openOrders,
              cuiCode,
            );

            const updated = await updateOrder(id, {
              ...result,
              physicalId,
              payloadId,
            });
            if (updated) done.push(updated);
            shouldContinue = false; // success, move to next
          } catch (err) {
            try {
              await ejectCard('REJECT');
            } catch (err2) {
              console.error(err2);
            }

            const shouldRetry = await new Promise((resolve) => {
              Modal.confirm({
                title: t('errors.cardPrintingFailed') || 'Eroare la tiparire',
                centered: true,
                okText: t('actions.retry') || 'Reîncearcă',
                cancelText: t('actions.cancelOrder') || 'Anulează comanda',
                onOk: () => resolve(true),
                onCancel: () => resolve(false),
              });
            });

            if (!shouldRetry) {
              handleCancelOrder();
              showMessage('success', 'Comanda a fost anulata cu succes!');
              setDisabled(false);
              return;
            }
          }
        }
      }
      if (done.length > 0) {
        showMessage('success', t('entity.pos.successMessages.printSuccess'));
      }
    } catch (err) {
      try {
        await ejectCard('REJECT');
      } catch (err2) {
        console.error(err2);
      }
      showMessage('error', err);
    }
    setDisabled(false);
    setOpenOrders(
      openOrders.map((oo) => done.find((d) => d.id === oo.id) || oo),
    );
  }, [
    shift.posId,
    openOrders,
    getPayload,
    downloadImages,
    updateOrder,
    handleCancelOrder,
    t,
  ]);

  return (
    <>
      <div className="pos">
        <div className="pos__wrapper">
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
            {openOrders.map((ord, idx) => (
              <Col xs={{ span: 8 }} lg={{ span: 4 }} key={ord.id}>
                <POSButton
                  title={
                    <>
                      {t('entity.pos.payment.cardTitle', {
                        count: idx + 1,
                        name: ord?.account?.userFullname || '',
                      })}
                      {ord?.status === 'CONFIRMED' && (
                        <CheckCircleOutlined
                          style={{ fontSize: '16px', color: '#24ad09' }}
                        />
                      )}
                    </>
                  }
                  color="blue"
                  id={ord.id}
                />
              </Col>
            ))}
          </Row>
          <Divider style={{ margin: '1rem 0' }} />
          <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
            <Col xs={{ span: 24 }} lg={{ span: 4 }}>
              <POSButton
                title={t('actions.orders.print')}
                onClick={beginPrinting}
                disabled={disabled}
                color="orange"
              />
            </Col>
          </Row>
          <Divider style={{ margin: '1rem 0' }} />
        </div>
      </div>
    </>
  );
};

export default PrintCard;
